import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Table, Card, Button, Badge, Form, Spinner, Row, Col } from "react-bootstrap";
import moment from "moment";
import { CONTACT_DRAFTS_PENDING } from "../../graphql/contactDraft/query";

/**
 * ຂໍ້ມູນຈັດສົ່ງທີ່ລູກຄ້າຍັງບໍ່ທັນຢືນຢັນ — the queue of contact details a buyer never confirmed.
 *
 * WHY THIS SCREEN HAS TO EXIST
 * ----------------------------
 * When a buyer sends their phone or address in chat, 4B parses it and asks them to confirm
 * with one tap. Most do. The ones who do not are the whole reason this page exists: their
 * draft is never discarded (that would lose the order) and never written to the order on
 * its own (that could send a parcel somewhere they never agreed to). It waits here instead,
 * for a human to finish — which is what shop staff already do by phone.
 *
 * Without this screen that design is only half-real: the escalation has nowhere to escalate
 * to, and a quarter of parsed addresses — the ones we could only partly read — would sit
 * unseen.
 *
 * READ-ONLY ON PURPOSE. The confirmation belongs to the buyer; what staff need here is to
 * know WHO to call and WHAT to read back to them.
 */

const STATUS_META = {
  MATCHED: { variant: "success", label: "ອ່ານທີ່ຢູ່ໄດ້ຄົບ" },
  PARTIAL: { variant: "warning", label: "ອ່ານໄດ້ບາງສ່ວນ" },
  AMBIGUOUS: { variant: "warning", label: "ຊື່ເມືອງຊ້ຳກັນ" },
  UNRESOLVED: { variant: "secondary", label: "ອ່ານທີ່ຢູ່ບໍ່ໄດ້" },
  SKIPPED_NO_PROVIDER: { variant: "secondary", label: "ບໍ່ມີຂົນສົ່ງເຊື່ອມ" },
};

/** 8 digits as people read them aloud. */
const prettyPhone = (p) => (p ? `020 ${p.slice(0, 4)} ${p.slice(4)}` : "—");

const ContactDrafts = () => {
  const shop = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("SHOP_DATA")) || {}; }
    catch (e) { return {}; }
  }, []);
  /**
   * `id`, not `_id`.
   *
   * SHOP_DATA is the serialised GraphQL shop, where the field is exposed as `id` — 54 places
   * in this app read `shop?.id` and exactly one reads `_id`. Reading the wrong one silently
   * yields undefined and the page reports "no shop" to someone who is perfectly well logged in.
   * `_id` is kept as a fallback only so a differently-shaped cached object still works.
   */
  const shopId = shop?.id || shop?._id;

  const [onlyOverdue, setOnlyOverdue] = useState(true);
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const { data, loading, error, refetch } = useQuery(CONTACT_DRAFTS_PENDING, {
    variables: { shopId, onlyOverdue, limit: LIMIT, offset: page * LIMIT },
    skip: !shopId,
    fetchPolicy: "network-only",
  });

  const rows = data?.contactDraftsPending?.data || [];
  const total = data?.contactDraftsPending?.total || 0;

  const waitedFor = (iso) => {
    if (!iso) return "—";
    const mins = moment().diff(moment(iso), "minutes");
    if (mins < 60) return `${mins} ນາທີ`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} ຊົ່ວໂມງ`;
    return `${Math.floor(hrs / 24)} ມື້`;
  };

  if (!shopId) {
    return (
      <div className="p-4">
        <Card body>ບໍ່ພົບຮ້ານ — ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່</Card>
      </div>
    );
  }

  return (
    <div className="p-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-1">ເບີ/ທີ່ຢູ່ຈາກແຊັດ ທີ່ລູກຄ້າຍັງບໍ່ທັນຢືນຢັນ</h4>
          <div className="text-muted" style={{ fontSize: 14 }}>
            ລູກຄ້າສົ່ງເບີ ຫຼື ທີ່ຢູ່ມາທາງແຊັດ ແລະ ລະບົບໄດ້ຂໍໃຫ້ຢືນຢັນແລ້ວ ແຕ່ຍັງບໍ່ທັນກົດ —
            ໂທຫາແລ້ວຢືນຢັນແທນໄດ້
          </div>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={() => refetch()} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "ໂຫຼດໃໝ່"}
          </Button>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body className="py-2">
          <Form.Check
            type="switch"
            id="only-overdue"
            label="ສະເພາະລາຍການທີ່ລໍດົນແລ້ວ"
            checked={onlyOverdue}
            onChange={(e) => { setOnlyOverdue(e.target.checked); setPage(0); }}
          />
          <div className="text-muted mt-1" style={{ fontSize: 13 }}>
            ປິດສະວິດນີ້ ເພື່ອເບິ່ງທຸກລາຍການທີ່ຍັງລໍຢືນຢັນ ລວມທັງອັນທີ່ຫາກໍຖາມໄປ
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Card body className="mb-3 text-danger">
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ — {String(error.message)}
        </Card>
      )}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>ທັງໝົດ {total} ລາຍການ</span>
          {loading && <Spinner animation="border" size="sm" />}
        </Card.Header>

        {/* The table scrolls inside its own box so the page never scrolls sideways. */}
        <div style={{ overflowX: "auto" }}>
          <Table hover responsive className="mb-0" style={{ minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: "nowrap" }}>ລໍມາແລ້ວ</th>
                <th style={{ whiteSpace: "nowrap" }}>ເບີໂທ</th>
                <th>ທີ່ຢູ່ທີ່ອ່ານໄດ້</th>
                <th>ຂໍ້ຄວາມຈາກລູກຄ້າ</th>
                <th style={{ whiteSpace: "nowrap" }}>ຂົນສົ່ງ</th>
                <th style={{ whiteSpace: "nowrap" }}>ສະຖານະ</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    ບໍ່ມີລາຍການລໍຢືນຢັນ
                  </td>
                </tr>
              )}

              {rows.map((d) => {
                const meta = STATUS_META[d.resolutionStatus] || STATUS_META.UNRESOLVED;
                const r = d.resolved || {};
                const addrBits = [
                  r.branch && `ສາຂາ ${r.branch}`,
                  r.village && `ບ້ານ ${r.village}`,
                  r.districtName && `ເມືອງ ${r.districtName}`,
                  r.provinceName && `ແຂວງ ${r.provinceName}`,
                ].filter(Boolean);

                return (
                  <tr key={d.id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <b>{waitedFor(d.summarySentAt)}</b>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {d.summarySentAt ? moment(d.summarySentAt).format("DD/MM HH:mm") : "—"}
                      </div>
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <b>{prettyPhone(d.parsedPhone)}</b>
                      {/* `bare` means eight digits with nothing around them to confirm it is a
                          phone at all — worth a second look before dialling. */}
                      {d.phoneConfidence === "bare" && (
                        <div><Badge variant="light" className="text-muted">ບໍ່ແນ່ໃຈ</Badge></div>
                      )}
                    </td>

                    <td>
                      {addrBits.length
                        ? addrBits.join(" · ")
                        : <span className="text-muted">ອ່ານບໍ່ໄດ້ — ອ່ານຈາກຂໍ້ຄວາມຂ້າງຂວາ</span>}
                    </td>

                    {/* Always shown, never replaced by our interpretation: on a partial read
                        what the buyer actually typed is what the staff member needs. */}
                    <td style={{ maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13 }}>
                      {d.rawAddressText || <span className="text-muted">—</span>}
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      {r.carrierName || <span className="text-muted">—</span>}
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {total > LIMIT && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <Button size="sm" variant="outline-secondary" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(p - 1, 0))}>ກ່ອນໜ້າ</Button>
            <span className="text-muted" style={{ fontSize: 13 }}>
              ໜ້າ {page + 1} / {Math.ceil(total / LIMIT)}
            </span>
            <Button size="sm" variant="outline-secondary"
              disabled={(page + 1) * LIMIT >= total}
              onClick={() => setPage((p) => p + 1)}>ຕໍ່ໄປ</Button>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default ContactDrafts;
